import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as TestIcon,
  Star as StarIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material'
import api from '../services/api'

interface RazorpayConfig {
  id: string
  configuration_name: string
  key_id: string
  test_mode: boolean
  is_default: boolean
  is_active: boolean
  payment_link_expiry_hours: number
  enabled_methods: string[]
  created_at: string
}

const paymentMethods = [
  { value: 'card', label: 'Card' },
  { value: 'netbanking', label: 'Net Banking' },
  { value: 'upi', label: 'UPI' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'emi', label: 'EMI' },
  { value: 'cardless_emi', label: 'Cardless EMI' },
  { value: 'paylater', label: 'Pay Later' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
]

const RazorpayConfigurationPage: React.FC = () => {
  const [configurations, setConfigurations] = useState<RazorpayConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<RazorpayConfig | null>(null)
  const [testResult, setTestResult] = useState<any>(null)
  
  const [formData, setFormData] = useState({
    configuration_name: '',
    key_id: '',
    key_secret: '',
    webhook_secret: '',
    test_mode: true,
    payment_link_expiry_hours: 24,
    accept_partial_payments: false,
    send_sms_notifications: true,
    send_email_notifications: true,
    enabled_methods: ['card', 'netbanking', 'upi', 'wallet'],
    callback_url: '',
    callback_method: 'get',
    is_default: false,
  })

  useEffect(() => {
    loadConfigurations()
  }, [])

  const loadConfigurations = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/admin/razorpay/configurations')
      setConfigurations(response.data.configurations)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load configurations')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (config?: RazorpayConfig) => {
    if (config) {
      setEditingConfig(config)
      // Load full config details
      loadConfigDetails(config.id)
    } else {
      setEditingConfig(null)
      setFormData({
        configuration_name: '',
        key_id: '',
        key_secret: '',
        webhook_secret: '',
        test_mode: true,
        payment_link_expiry_hours: 24,
        accept_partial_payments: false,
        send_sms_notifications: true,
        send_email_notifications: true,
        enabled_methods: ['card', 'netbanking', 'upi', 'wallet'],
        callback_url: '',
        callback_method: 'get',
        is_default: false,
      })
    }
    setDialogOpen(true)
    setTestResult(null)
  }

  const loadConfigDetails = async (configId: string) => {
    try {
      const response = await api.get(`/admin/razorpay/configurations/${configId}`)
      const config = response.data
      setFormData({
        configuration_name: config.configuration_name,
        key_id: config.key_id,
        key_secret: '', // Don't load secret for security
        webhook_secret: '',
        test_mode: config.test_mode,
        payment_link_expiry_hours: config.payment_link_expiry_hours,
        accept_partial_payments: config.accept_partial_payments,
        send_sms_notifications: config.send_sms_notifications,
        send_email_notifications: config.send_email_notifications,
        enabled_methods: config.enabled_methods,
        callback_url: config.callback_url || '',
        callback_method: config.callback_method,
        is_default: config.is_default,
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load configuration details')
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingConfig(null)
    setTestResult(null)
  }

  const handleSave = async () => {
    try {
      if (editingConfig) {
        // Update existing
        const updateData: any = {
          configuration_name: formData.configuration_name,
          test_mode: formData.test_mode,
          payment_link_expiry_hours: formData.payment_link_expiry_hours,
          accept_partial_payments: formData.accept_partial_payments,
          send_sms_notifications: formData.send_sms_notifications,
          send_email_notifications: formData.send_email_notifications,
          enabled_methods: formData.enabled_methods,
          callback_url: formData.callback_url || null,
          callback_method: formData.callback_method,
          is_default: formData.is_default,
        }
        
        // Only include secrets if they were changed
        if (formData.key_id) updateData.key_id = formData.key_id
        if (formData.key_secret) updateData.key_secret = formData.key_secret
        if (formData.webhook_secret) updateData.webhook_secret = formData.webhook_secret
        
        await api.put(`/admin/razorpay/configurations/${editingConfig.id}`, updateData)
        setSuccess('Configuration updated successfully')
      } else {
        // Create new
        await api.post('/admin/razorpay/configurations', formData)
        setSuccess('Configuration created successfully')
      }
      
      handleCloseDialog()
      loadConfigurations()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return
    
    try {
      await api.delete(`/admin/razorpay/configurations/${id}`)
      setSuccess('Configuration deleted successfully')
      loadConfigurations()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete configuration')
    }
  }

  const handleTest = async (id: string) => {
    try {
      setTestResult(null)
      const response = await api.post(`/admin/razorpay/configurations/${id}/test`)
      setTestResult(response.data)
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Test failed'
      })
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PaymentIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h4">
              Razorpay Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure Razorpay payment gateway for payment links and online payments
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Configuration
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Info Card */}
      <Card sx={{ mb: 3, bgcolor: 'info.lighter' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            About Razorpay Integration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Razorpay allows you to generate payment links for invoices, accept online payments,
            and manage refunds. You can configure multiple environments (test and live) and
            customize payment methods, notifications, and expiry settings.
          </Typography>
        </CardContent>
      </Card>

      {/* Configurations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Configuration Name</TableCell>
              <TableCell>Key ID</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Payment Methods</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : configurations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No configurations found. Add your first Razorpay configuration to get started.
                </TableCell>
              </TableRow>
            ) : (
              configurations.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.configuration_name}
                      {config.is_default && (
                        <Chip icon={<StarIcon />} label="Default" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {config.key_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.test_mode ? 'Test' : 'Live'}
                      size="small"
                      color={config.test_mode ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell>{config.payment_link_expiry_hours}h</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {config.enabled_methods.length} methods
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={config.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Test Configuration">
                      <IconButton
                        size="small"
                        onClick={() => handleTest(config.id)}
                      >
                        <TestIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Configuration">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(config)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Configuration">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(config.id)}
                        disabled={config.is_default}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Razorpay Configuration' : 'Add Razorpay Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Configuration Name"
                value={formData.configuration_name}
                onChange={(e) => setFormData({ ...formData, configuration_name: e.target.value })}
                required
                helperText="A friendly name for this configuration"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Razorpay Key ID"
                value={formData.key_id}
                onChange={(e) => setFormData({ ...formData, key_id: e.target.value })}
                required={!editingConfig}
                helperText={editingConfig ? 'Leave empty to keep current' : 'Your Razorpay API Key ID'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Razorpay Key Secret"
                value={formData.key_secret}
                onChange={(e) => setFormData({ ...formData, key_secret: e.target.value })}
                required={!editingConfig}
                helperText={editingConfig ? 'Leave empty to keep current' : 'Your Razorpay API Key Secret'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Webhook Secret"
                value={formData.webhook_secret}
                onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
                helperText="Optional: For webhook signature verification"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.test_mode}
                    onChange={(e) => setFormData({ ...formData, test_mode: e.target.checked })}
                  />
                }
                label="Test Mode"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                }
                label="Set as Default"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Payment Link Expiry (hours)"
                value={formData.payment_link_expiry_hours}
                onChange={(e) => setFormData({ ...formData, payment_link_expiry_hours: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 720 }}
                helperText="1-720 hours (max 30 days)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Callback Method</InputLabel>
                <Select
                  value={formData.callback_method}
                  onChange={(e) => setFormData({ ...formData, callback_method: e.target.value })}
                  label="Callback Method"
                >
                  <MenuItem value="get">GET</MenuItem>
                  <MenuItem value="post">POST</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Callback URL"
                value={formData.callback_url}
                onChange={(e) => setFormData({ ...formData, callback_url: e.target.value })}
                helperText="Optional: URL to redirect after payment"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Enabled Payment Methods</InputLabel>
                <Select
                  multiple
                  value={formData.enabled_methods}
                  onChange={(e) => setFormData({ ...formData, enabled_methods: e.target.value as string[] })}
                  input={<OutlinedInput label="Enabled Payment Methods" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      <Checkbox checked={formData.enabled_methods.indexOf(method.value) > -1} />
                      <ListItemText primary={method.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.accept_partial_payments}
                    onChange={(e) => setFormData({ ...formData, accept_partial_payments: e.target.checked })}
                  />
                }
                label="Accept Partial Payments"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.send_sms_notifications}
                    onChange={(e) => setFormData({ ...formData, send_sms_notifications: e.target.checked })}
                  />
                }
                label="Send SMS Notifications"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.send_email_notifications}
                    onChange={(e) => setFormData({ ...formData, send_email_notifications: e.target.checked })}
                  />
                }
                label="Send Email Notifications"
              />
            </Grid>

            {testResult && (
              <Grid item xs={12}>
                <Alert severity={testResult.success ? 'success' : 'error'}>
                  {testResult.message}
                  {testResult.warning && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Warning: {testResult.warning}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {editingConfig && (
            <Button onClick={() => handleTest(editingConfig.id)} startIcon={<TestIcon />}>
              Test
            </Button>
          )}
          <Button onClick={handleSave} variant="contained">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RazorpayConfigurationPage
